import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { ChevronDown, Calendar, Clock, Users, Mail, Phone, MapPin, Instagram, Star, Menu as MenuIcon, X, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language context
const LanguageContext = React.createContext();

const translations = {
  fr: {
    home: 'Accueil',
    menu: 'Notre Menu',
    reservation: 'Réserver',
    reviews: 'Avis',
    contact: 'Contact',
    admin: 'Admin',
    welcome: 'Bienvenue au',
    description: 'Brasserie conviviale à Beaubourg',
    bookTable: 'Réserver une table',
    viewMenu: 'Voir le menu',
    aboutTitle: 'Une expérience bistronomique unique',
    aboutText: 'Situé au cœur de Beaubourg, à deux pas du Centre Pompidou, Le Parvis vous accueille dans une ambiance chaleureuse et conviviale. Profitez de notre terrasse ensoleillée et de notre large choix de bières, cocktails et planches de dégustation. Avec un Happy Hour quotidien de 16h à 22h et une cuisine française authentique, nous sommes l\'adresse idéale pour un afterwork, un repas entre amis ou un événement. Ouvert tous les jours de 8h à 2h du matin.',
    testimonials: 'Avis de nos clients',
    gallery: 'Galerie',
    ourMenu: 'Notre Menu',
    reserveTable: 'Réserver une table',
    name: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    date: 'Date',
    time: 'Heure',
    guests: 'Nombre de personnes',
    message: 'Message (optionnel)',
    submit: 'Envoyer',
    address: 'Adresse',
    openingHours: 'Horaires',
    followUs: 'Suivez-nous',
    allRights: 'Tous droits réservés',
    customerReviews: 'Avis clients',
    loginAdmin: 'Connexion Admin',
    username: 'Nom d\'utilisateur',
    password: 'Mot de passe',
    login: 'Se connecter'
  },
  en: {
    home: 'Home',
    menu: 'Our Menu',
    reservation: 'Book',
    reviews: 'Reviews',
    contact: 'Contact',
    admin: 'Admin',
    welcome: 'Welcome to',
    description: 'Friendly brasserie in Beaubourg',
    bookTable: 'Book a table',
    viewMenu: 'View menu',
    aboutTitle: 'A unique bistronomic experience',
    aboutText: 'Located in the heart of Beaubourg, steps from the Pompidou Center, Le Parvis welcomes you in a warm and friendly atmosphere. Enjoy our sunny terrace and wide selection of beers, cocktails and sharing platters. With daily Happy Hour from 4pm to 10pm and authentic French cuisine, we are the ideal spot for afterwork, meals with friends or events. Open daily from 8am to 2am.',
    testimonials: 'Customer reviews',
    gallery: 'Gallery',
    ourMenu: 'Our Menu',
    reserveTable: 'Book a table',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    date: 'Date',
    time: 'Time',
    guests: 'Number of guests',
    message: 'Message (optional)',
    submit: 'Submit',
    address: 'Address',
    openingHours: 'Opening Hours',
    followUs: 'Follow Us',
    allRights: 'All rights reserved',
    customerReviews: 'Customer Reviews',
    loginAdmin: 'Admin Login',
    username: 'Username',
    password: 'Password',
    login: 'Login'
  },
  es: {
    home: 'Inicio',
    menu: 'Nuestro Menú',
    reservation: 'Reservar',
    reviews: 'Reseñas',
    contact: 'Contacto',
    admin: 'Admin',
    welcome: 'Bienvenido a',
    description: 'Restaurante bistronómico en el corazón de Montmartre',
    bookTable: 'Reservar mesa',
    viewMenu: 'Ver menú',
    aboutTitle: 'Una experiencia bistronómica única',
    aboutText: 'Ubicado en el corazón de Beaubourg, cerca del Centro Pompidou, Le Parvis le da la bienvenida en un ambiente cálido y acogedor. Disfrute de nuestra terraza soleada y amplia selección de cervezas, cócteles y tablas de degustación. Con Happy Hour diario de 16h a 22h y auténtica cocina francesa, somos el lugar ideal para afterwork, comidas con amigos o eventos. Abierto todos los días de 8h a 2h de la madrugada.',
    testimonials: 'Opiniones de clientes',
    gallery: 'Galería',
    ourMenu: 'Nuestro Menú',
    reserveTable: 'Reservar mesa',
    name: 'Nombre',
    email: 'Email',
    phone: 'Teléfono',
    date: 'Fecha',
    time: 'Hora',
    guests: 'Número de personas',
    message: 'Mensaje (opcional)',
    submit: 'Enviar',
    address: 'Dirección',
    openingHours: 'Horario',
    followUs: 'Síguenos',
    allRights: 'Todos los derechos reservados',
    customerReviews: 'Reseñas de clientes',
    loginAdmin: 'Inicio de sesión de administrador',
    username: 'Nombre de usuario',
    password: 'Contraseña',
    login: 'Iniciar sesión'
  },
  it: {
    home: 'Home',
    menu: 'Il Nostro Menu',
    reservation: 'Prenota',
    reviews: 'Recensioni',
    contact: 'Contatto',
    admin: 'Admin',
    welcome: 'Benvenuto a',
    description: 'Ristorante bistronomico nel cuore di Montmartre',
    bookTable: 'Prenota un tavolo',
    viewMenu: 'Vedi menu',
    aboutTitle: 'Un\'esperienza bistronomica unica',
    aboutText: 'Situato nel cuore di Beaubourg, vicino al Centro Pompidou, Le Parvis vi accoglie in un\'atmosfera calda e conviviale. Godetevi la nostra terrazza soleggiata e l\'ampia selezione di birre, cocktail e taglieri. Con Happy Hour quotidiano dalle 16h alle 22h e autentica cucina francese, siamo il luogo ideale per l\'afterwork, pranzi con amici o eventi. Aperto tutti i giorni dalle 8h alle 2h del mattino.',
    testimonials: 'Recensioni clienti',
    gallery: 'Galleria',
    ourMenu: 'Il Nostro Menu',
    reserveTable: 'Prenota un tavolo',
    name: 'Nome',
    email: 'Email',
    phone: 'Telefono',
    date: 'Data',
    time: 'Ora',
    guests: 'Numero di ospiti',
    message: 'Messaggio (opzionale)',
    submit: 'Invia',
    address: 'Indirizzo',
    openingHours: 'Orari',
    followUs: 'Seguici',
    allRights: 'Tutti i diritti riservati',
    customerReviews: 'Recensioni clienti',
    loginAdmin: 'Login amministratore',
    username: 'Nome utente',
    password: 'Password',
    login: 'Accedi'
  },
  de: {
    home: 'Startseite',
    menu: 'Unsere Speisekarte',
    reservation: 'Reservieren',
    reviews: 'Bewertungen',
    contact: 'Kontakt',
    admin: 'Admin',
    welcome: 'Willkommen bei',
    description: 'Bistronomisches Restaurant im Herzen von Montmartre',
    bookTable: 'Tisch reservieren',
    viewMenu: 'Speisekarte ansehen',
    aboutTitle: 'Ein einzigartiges bistronomisches Erlebnis',
    aboutText: 'Im Herzen von Beaubourg gelegen, in der Nähe des Centre Pompidou, empfängt Sie Le Parvis in einer warmen und freundlichen Atmosphäre. Genießen Sie unsere sonnige Terrasse und große Auswahl an Bieren, Cocktails und Degustationsplatten. Mit täglicher Happy Hour von 16 bis 22 Uhr und authentischer französischer Küche sind wir die ideale Adresse für Afterwork, Essen mit Freunden oder Events. Täglich von 8 bis 2 Uhr morgens geöffnet.',
    testimonials: 'Kundenbewertungen',
    gallery: 'Galerie',
    ourMenu: 'Unsere Speisekarte',
    reserveTable: 'Tisch reservieren',
    name: 'Name',
    email: 'E-Mail',
    phone: 'Telefon',
    date: 'Datum',
    time: 'Uhrzeit',
    guests: 'Anzahl der Gäste',
    message: 'Nachricht (optional)',
    submit: 'Senden',
    address: 'Adresse',
    openingHours: 'Öffnungszeiten',
    followUs: 'Folgen Sie uns',
    allRights: 'Alle Rechte vorbehalten',
    customerReviews: 'Kundenbewertungen',
    loginAdmin: 'Admin-Login',
    username: 'Benutzername',
    password: 'Passwort',
    login: 'Anmelden'
  },
  pt: {
    home: 'Início',
    menu: 'Nosso Menu',
    reservation: 'Reservar',
    reviews: 'Avaliações',
    contact: 'Contato',
    admin: 'Admin',
    welcome: 'Bem-vindo ao',
    description: 'Restaurante gastronômico no coração de Montmartre',
    bookTable: 'Reservar mesa',
    viewMenu: 'Ver menu',
    aboutTitle: 'Uma experiência gastronômica única',
    aboutText: 'Localizado no coração de Beaubourg, perto do Centro Pompidou, o Le Parvis acolhe-o num ambiente caloroso e acolhedor. Aproveite o nosso terraço ensolarado e ampla seleção de cervejas, cocktails e tábuas de degustação. Com Happy Hour diário das 16h às 22h e autêntica cozinha francesa, somos o local ideal para afterwork, refeições com amigos ou eventos. Aberto todos os dias das 8h às 2h da manhã.',
    testimonials: 'Avaliações dos clientes',
    gallery: 'Galeria',
    ourMenu: 'Nosso Menu',
    reserveTable: 'Reservar mesa',
    name: 'Nome',
    email: 'Email',
    phone: 'Telefone',
    date: 'Data',
    time: 'Hora',
    guests: 'Número de pessoas',
    message: 'Mensagem (opcional)',
    submit: 'Enviar',
    address: 'Endereço',
    openingHours: 'Horário',
    followUs: 'Siga-nos',
    allRights: 'Todos os direitos reservados',
    customerReviews: 'Avaliações de clientes',
    loginAdmin: 'Login Admin',
    username: 'Nome de usuário',
    password: 'Senha',
    login: 'Entrar'
  },
  ru: {
    home: 'Главная',
    menu: 'Наше меню',
    reservation: 'Забронировать',
    reviews: 'Отзывы',
    contact: 'Контакты',
    admin: 'Админ',
    welcome: 'Добро пожаловать в',
    description: 'Гастрономический ресторан в сердце Монмартра',
    bookTable: 'Забронировать столик',
    viewMenu: 'Посмотреть меню',
    aboutTitle: 'Уникальный гастрономический опыт',
    aboutText: 'Расположенный в самом сердце Бобура, рядом с центром Помпиду, Le Parvis приглашает вас в теплую и дружескую атмосферу. Наслаждайтесь нашей солнечной террасой и широким выбором пива, коктейлей и закусок. С ежедневным счастливым часом с 16:00 до 22:00 и аутентичной французской кухней, мы идеальное место для афтерворка, встреч с друзьями или мероприятий. Открыто каждый день с 8:00 до 2:00 ночи.',
    testimonials: 'Отзывы клиентов',
    gallery: 'Галерея',
    ourMenu: 'Наше меню',
    reserveTable: 'Забронировать столик',
    name: 'Имя',
    email: 'Email',
    phone: 'Телефон',
    date: 'Дата',
    time: 'Время',
    guests: 'Количество гостей',
    message: 'Сообщение (необязательно)',
    submit: 'Отправить',
    address: 'Адрес',
    openingHours: 'Часы работы',
    followUs: 'Следите за нами',
    allRights: 'Все права защищены',
    customerReviews: 'Отзывы клиентов',
    loginAdmin: 'Вход для администратора',
    username: 'Имя пользователя',
    password: 'Пароль',
    login: 'Войти'
  },
  zh: {
    home: '首页',
    menu: '我们的菜单',
    reservation: '预订',
    reviews: '评论',
    contact: '联系',
    admin: '管理',
    welcome: '欢迎来到',
    description: '蒙马特中心的美食餐厅',
    bookTable: '预订餐桌',
    viewMenu: '查看菜单',
    aboutTitle: '独特的美食体验',
    aboutText: '位于博堡区中心，靠近蓬皮杜中心，Le Parvis 在温馨友好的氛围中欢迎您。享受我们阳光明媚的露台以及啤酒、鸡尾酒和分享拼盘的丰富选择。每日欢乐时光从下午4点到晚上10点，提供正宗的法国美食，我们是下班后聚会、朋友聚餐或活动的理想场所。每天从早上8点营业到凌晨2点。',
    testimonials: '客户评价',
    gallery: '画廊',
    ourMenu: '我们的菜单',
    reserveTable: '预订餐桌',
    name: '姓名',
    email: '电子邮件',
    phone: '电话',
    date: '日期',
    time: '时间',
    guests: '人数',
    message: '留言（可选）',
    submit: '提交',
    address: '地址',
    openingHours: '营业时间',
    followUs: '关注我们',
    allRights: '版权所有',
    customerReviews: '客户评价',
    loginAdmin: '管理员登录',
    username: '用户名',
    password: '密码',
    login: '登录'
  },
  ja: {
    home: 'ホーム',
    menu: 'メニュー',
    reservation: '予約',
    reviews: 'レビュー',
    contact: 'お問い合わせ',
    admin: '管理',
    welcome: 'ようこそ',
    description: 'モンマルトルの中心にある美食レストラン',
    bookTable: 'テーブルを予約',
    viewMenu: 'メニューを見る',
    aboutTitle: 'ユニークな美食体験',
    aboutText: 'ポンピドゥーセンター近くのボーブール地区の中心に位置するLe Parvisは、温かく親しみやすい雰囲気でお客様をお迎えします。日当たりの良いテラスで、ビール、カクテル、シェアプレートの豊富な品揃えをお楽しみください。毎日午後4時から午後10時までのハッピーアワーと本格的なフランス料理で、アフターワーク、友人との食事、イベントに最適な場所です。毎日午前8時から午前2時まで営業。',
    testimonials: 'お客様の声',
    gallery: 'ギャラリー',
    ourMenu: 'メニュー',
    reserveTable: 'テーブルを予約',
    name: '名前',
    email: 'メール',
    phone: '電話',
    date: '日付',
    time: '時間',
    guests: '人数',
    message: 'メッセージ（任意）',
    submit: '送信',
    address: '住所',
    openingHours: '営業時間',
    followUs: 'フォロー',
    allRights: '全著作権所有',
    customerReviews: 'お客様のレビュー',
    loginAdmin: '管理者ログイン',
    username: 'ユーザー名',
    password: 'パスワード',
    login: 'ログイン'
  },
  th: {
    home: 'หน้าแรก',
    menu: 'เมนูของเรา',
    reservation: 'จอง',
    reviews: 'รีวิว',
    contact: 'ติดต่อ',
    admin: 'ผู้ดูแล',
    welcome: 'ยินดีต้อนรับสู่',
    description: 'ร้านอาหารระดับโลกในใจกลางมงมาร์ตร์',
    bookTable: 'จองโต๊ะ',
    viewMenu: 'ดูเมนู',
    aboutTitle: 'ประสบการณ์อาหารที่ไม่เหมือนใคร',
    aboutText: 'ตั้งอยู่ใจกลางโบบูร์ ใกล้ศูนย์ปงปิดู Le Parvis ยินดีต้อนรับคุณด้วยบรรยากาศที่อบอุ่นและเป็นกันเอง เพลิดเพลินกับระเบียงที่มีแสงแดดและเครื่องดื่มมากมาย เช่น เบียร์ ค็อกเทล และจานแชร์ ด้วยแฮปปี้อาวร์ทุกวันตั้งแต่ 16.00-22.00 น. และอาหารฝรั่งเศสแท้ เราเป็นสถานที่ที่เหมาะสำหรับหลังเลิกงาน รับประทานอาหารกับเพื่อน หรือจัดงาน เปิดทุกวันตั้งแต่ 8.00 น. ถึง 2.00 น.',
    testimonials: 'รีวิวจากลูกค้า',
    gallery: 'แกลเลอรี',
    ourMenu: 'เมนูของเรา',
    reserveTable: 'จองโต๊ะ',
    name: 'ชื่อ',
    email: 'อีเมล',
    phone: 'โทรศัพท์',
    date: 'วันที่',
    time: 'เวลา',
    guests: 'จำนวนผู้เข้าร่วม',
    message: 'ข้อความ (ไม่บังคับ)',
    submit: 'ส่ง',
    address: 'ที่อยู่',
    openingHours: 'เวลาทำการ',
    followUs: 'ติดตามเรา',
    allRights: 'สงวนลิขสิทธิ์',
    customerReviews: 'รีวิวจากลูกค้า',
    loginAdmin: 'เข้าสู่ระบบผู้ดูแล',
    username: 'ชื่อผู้ใช้',
    password: 'รหัสผ่าน',
    login: 'เข้าสู่ระบบ'
  },
  ar: {
    home: 'الرئيسية',
    menu: 'قائمتنا',
    reservation: 'حجز',
    reviews: 'التقييمات',
    contact: 'اتصل بنا',
    admin: 'المشرف',
    welcome: 'مرحبا بكم في',
    description: 'مطعم فاخر في قلب مونمارتر',
    bookTable: 'احجز طاولة',
    viewMenu: 'عرض القائمة',
    aboutTitle: 'تجربة طهي فريدة',
    aboutText: 'يقع Le Parvis في قلب بوبور، بالقرب من مركز بومبيدو، ويرحب بكم في أجواء دافئة وودية. استمتع بشرفتنا المشمسة ومجموعة واسعة من البيرة والكوكتيلات وأطباق المشاركة. مع ساعة سعيدة يومية من الساعة 4 مساءً حتى 10 مساءً ومطبخ فرنسي أصيل، نحن المكان المثالي لما بعد العمل، الوجبات مع الأصدقاء أو الفعاليات. مفتوح يوميًا من الساعة 8 صباحًا حتى 2 صباحًا.',
    testimonials: 'آراء العملاء',
    gallery: 'معرض الصور',
    ourMenu: 'قائمتنا',
    reserveTable: 'احجز طاولة',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    date: 'التاريخ',
    time: 'الوقت',
    guests: 'عدد الأشخاص',
    message: 'رسالة (اختياري)',
    submit: 'إرسال',
    address: 'العنوان',
    openingHours: 'ساعات العمل',
    followUs: 'تابعنا',
    allRights: 'جميع الحقوق محفوظة',
    customerReviews: 'تقييمات العملاء',
    loginAdmin: 'تسجيل دخول المشرف',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    login: 'تسجيل الدخول'
  }
};

const Header = () => {
  const { language, setLanguage, t } = React.useContext(LanguageContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const languages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'it', label: 'Italiano' },
    { code: 'de', label: 'Deutsch' },
    { code: 'pt', label: 'Português' },
    { code: 'ru', label: 'Русский' },
    { code: 'zh', label: '中文' },
    { code: 'ja', label: '日本語' },
    { code: 'th', label: 'ไทย' },
    { code: 'ar', label: 'العربية' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all bg-white/95 backdrop-blur-md shadow-lg">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-2xl font-display font-bold text-burgundy">
            Le Parvis Pompidou
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link" data-testid="nav-home">{t.home}</Link>
            <Link to="/menu" className="nav-link" data-testid="nav-menu">{t.menu}</Link>
            <Link to="/reservation" className="nav-link" data-testid="nav-reservation">{t.reservation}</Link>
            <Link to="/contact" className="nav-link" data-testid="nav-contact">{t.contact}</Link>
          </div>

          {/* Language Selector */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-burgundy" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent border border-burgundy text-burgundy rounded-lg px-3 py-1 text-sm cursor-pointer"
              data-testid="language-selector"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-burgundy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={28} /> : <MenuIcon size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3 bg-white/95 backdrop-blur-md rounded-lg p-4 shadow-lg" data-testid="mobile-menu">
            <Link to="/" className="block text-burgundy font-medium py-2 px-3 rounded hover:bg-burgundy/10 transition" onClick={() => setMobileMenuOpen(false)}>{t.home}</Link>
            <Link to="/menu" className="block text-burgundy font-medium py-2 px-3 rounded hover:bg-burgundy/10 transition" onClick={() => setMobileMenuOpen(false)}>{t.menu}</Link>
            <Link to="/reservation" className="block text-burgundy font-medium py-2 px-3 rounded hover:bg-burgundy/10 transition" onClick={() => setMobileMenuOpen(false)}>{t.reservation}</Link>
            <Link to="/contact" className="block text-burgundy font-medium py-2 px-3 rounded hover:bg-burgundy/10 transition" onClick={() => setMobileMenuOpen(false)}>{t.contact}</Link>
          </div>
        )}
      </nav>
    </header>
  );
};

const Footer = () => {
  const { t } = React.useContext(LanguageContext);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(console.error);
  }, []);

  if (!settings) return null;

  return (
    <footer className="bg-burgundy text-cream py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-2xl font-display font-bold mb-4">{settings.restaurant_name.fr}</h3>
            <p className="text-cream/80">{settings.description.fr}</p>
          </div>
          <div>
            <h4 className="font-semibold mb-3">{t.contact}</h4>
            <div className="space-y-2 text-cream/80">
              <p className="flex items-center gap-2"><MapPin size={16} /> {settings.address}</p>
              <p className="flex items-center gap-2"><Phone size={16} /> {settings.phone}</p>
              <p className="flex items-center gap-2"><Mail size={16} /> {settings.email}</p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-3">{t.followUs}</h4>
            <a
              href={settings.social_media.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-cream/80 hover:text-gold transition"
              data-testid="instagram-link"
            >
              <Instagram size={20} /> Instagram
            </a>
          </div>
        </div>
        <div className="border-t border-cream/20 mt-8 pt-8 text-center text-cream/60 text-sm">
          <p>© 2025 Le Parvis Pompidou. {t.allRights}.</p>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  const { language, t } = React.useContext(LanguageContext);
  const navigate = useNavigate();
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [instagramLoading, setInstagramLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/instagram/posts`)
      .then(res => {
        setInstagramPosts(res.data.slice(0, 6));
      })
      .catch(console.error)
      .finally(() => setInstagramLoading(false));
  }, []);

  const images = {
    hero: 'https://customer-assets.emergentagent.com/job_beaubourg-square/artifacts/2mijw1jv_terrasse-exterieure.jpg',
    about: 'https://privateaser-media.s3.eu-west-1.amazonaws.com/etab_photos/51300/1500x750/425918.jpg',
    gallery1: 'https://privateaser-media.s3.eu-west-1.amazonaws.com/etab_photos/51300/1500x750/425921.jpg',
    gallery2: 'https://privateaser-media.s3.eu-west-1.amazonaws.com/etab_photos/51300/1500x750/425930.jpg',
    gallery3: 'https://privateaser-media.s3.eu-west-1.amazonaws.com/etab_photos/51300/1500x750/425916.jpg'
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section relative h-screen flex items-center justify-center text-white">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${images.hero})` }}></div>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-4" data-testid="hero-section">
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-4 text-cream">Le Parvis Pompidou</h1>
          <p className="text-xl md:text-2xl mb-8 text-cream/90">{t.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="btn-primary"
              onClick={() => navigate('/reservation')}
              data-testid="cta-book-table"
            >
              {t.bookTable}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="btn-secondary"
              onClick={() => navigate('/menu')}
              data-testid="cta-view-menu"
            >
              {t.viewMenu}
            </Button>
          </div>
          {/* Google Reviews Badge */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="https://www.google.com/maps/place/Le+Parvis+-+Beaubourg/@48.8618299,2.3480686,17z/data=!3m1!4b1!4m6!3m5!1s0x47e66e1c1a3734d5:0x45299cbff9deba9c!8m2!3d48.8618264!4d2.3506435!16s%2Fg%2F11bw3y4qxg" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 border border-gray-100"
            >
              {/* Google Logo */}
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              
              {/* Rating and Reviews */}
              <div className="text-left">
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-lg font-bold text-gray-900">4.0</span>
                  <div className="flex items-center">
                    {[1, 2, 3, 4].map((star) => (
                      <Star key={star} size={16} className="fill-yellow-400 text-yellow-400" />
                    ))}
                    <Star size={16} className="text-yellow-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-600 font-medium">750+ avis Google</p>
              </div>
            </a>
            
            {/* Write Review Button */}
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJBc00Ghxu5kcRnLre-c-5KUU"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 font-semibold text-sm"
            >
              <Star size={18} className="fill-white" />
              Laisser un avis
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={32} className="text-cream" />
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-burgundy mb-6">{t.aboutTitle}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">{t.aboutText}</p>

              <Button className="btn-primary" onClick={() => navigate('/menu')} data-testid="about-view-menu">
                {t.viewMenu}
              </Button>
            </div>
            <div className="order-1 md:order-2">
              <img
                src={images.about}
                alt="Restaurant interior"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-burgundy text-center mb-12">{t.gallery}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {instagramPosts.length > 0 ? (
              instagramPosts.map((post, idx) => (
                <a 
                  key={post.id || idx}
                  href={post.post_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="overflow-hidden rounded-lg shadow-lg hover-scale block"
                >
                  <img 
                    src={post.image_url?.startsWith('/') ? `${BACKEND_URL}${post.image_url}` : post.image_url} 
                    alt={post.caption || `Instagram post ${idx + 1}`} 
                    className="w-full h-[300px] object-cover" 
                  />
                </a>
              ))
            ) : (
              [images.gallery1, images.gallery2, images.gallery3].map((img, idx) => (
                <div key={idx} className="overflow-hidden rounded-lg shadow-lg hover-scale">
                  <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-[300px] object-cover" />
                </div>
              ))
            )}
          </div>
          {instagramPosts.length > 0 && (
            <div className="text-center mt-8">
              <a
                href="https://www.instagram.com/leparvis.beaubourg/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-burgundy hover:text-gold transition font-semibold"
              >
                <Instagram size={20} /> Voir plus sur Instagram
              </a>
            </div>
          )}
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-display font-bold text-burgundy mb-8 text-center">
              Le Parvis Pompidou - Brasserie Face au Centre Pompidou Paris
            </h2>
            
            <div className="bg-white rounded-lg shadow-md p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Situé au <strong>109 rue Saint-Martin à Beaubourg</strong>, face au <strong>Centre Pompidou</strong>, Le Parvis Pompidou vous accueille dans une <strong>ambiance chaleureuse et festive</strong> avec une <strong>cuisine française authentique</strong>. Notre établissement est le lieu idéal pour vos afterworks, repas entre amis et événements.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Notre Carte - Cuisine de Brasserie et Planches de Dégustation
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Découvrez notre <strong>carte variée</strong> offrant un excellent rapport qualité-prix. Spécialités maison : <strong>planches de charcuteries</strong>, <strong>fromages affinés</strong>, <strong>tapas</strong>, et plats traditionnels comme le <strong>bœuf bourguignon</strong>, la <strong>soupe à l'oignon gratinée</strong> ou nos <strong>croques-monsieur</strong> au pain brioché maison.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Bar et Happy Hour à Beaubourg
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Le Parvis propose un <strong>Happy Hour quotidien de 16h à 22h</strong> pour profiter de nos <strong>cocktails</strong>, <strong>bières pression</strong> et <strong>spiritueux</strong> à prix réduits. Avec sa <strong>diffusion des matchs</strong> sur grand écran, c'est l'adresse parfaite pour les amateurs de sport.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Terrasse Ensoleillée et Espaces Privatisables
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Profitez de notre <strong>terrasse ensoleillée</strong> pour un repas en plein air au cœur de Paris. Nous proposons également <strong>plusieurs espaces privatisables</strong> : Le Barnum, La Terrasse, La Boutique et L'Espace Pompidou, parfaits pour vos <strong>anniversaires</strong>, <strong>pots de départ</strong> ou <strong>événements d'entreprise</strong> jusqu'à 100 personnes.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Horaires Étendus - Ouvert 7j/7
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ouvert <strong>tous les jours de 8h à 2h du matin</strong>, Le Parvis est l'adresse idéale pour un <strong>petit-déjeuner</strong>, un <strong>déjeuner</strong>, un <strong>afterwork</strong> ou un <strong>dîner festif</strong>. Notre large amplitude horaire permet de vous accueillir à tout moment de la journée et de la soirée.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Le Rendez-vous de Beaubourg
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Habitués du quartier</strong> et <strong>visiteurs</strong> se retrouvent au Parvis pour des moments conviviaux autour d'un verre ou d'un repas. Avec ses <strong>5/5 étoiles sur Privateaser</strong> et ses <strong>914 avis Google</strong>, notre établissement est devenu une référence à Beaubourg.
              </p>
              
              <h3 className="text-2xl font-display font-bold text-burgundy mb-4 mt-8">
                Localisation Idéale à Paris 4ème - Quartier Beaubourg
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Situé dans le <strong>4ème arrondissement de Paris</strong>, au <strong>109 rue Saint-Martin</strong>, Le Parvis Pompidou bénéficie d'un emplacement privilégié face au <strong>Centre Pompidou</strong>, à proximité des <strong>Halles</strong> et du <strong>Marais</strong>. Facilement accessible via les métros <strong>Rambuteau</strong> (ligne 11) et <strong>Châtelet-Les Halles</strong> (RER A, B, D).
              </p>
              
              <div className="bg-burgundy/5 border border-burgundy/20 rounded-lg p-6 mt-8">
                <h3 className="text-xl font-display font-bold text-burgundy mb-3">
                  Pourquoi choisir Le Parvis ?
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Brasserie française authentique</strong> avec planches et tapas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Happy Hour quotidien 16h-22h</strong> - Bières et cocktails</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Terrasse ensoleillée</strong> au cœur de Beaubourg</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Ouvert 7j/7 de 8h à 2h</strong> - Idéal pour afterwork</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Espaces privatisables</strong> jusqu'à 100 personnes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Diffusion des matchs</strong> sur grand écran</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-burgundy mt-1">✓</span>
                    <span><strong>Localisation idéale</strong> près du Centre Pompidou</span>
                  </li>
                </ul>
              </div>
              
              <div className="text-center mt-8">
                <Button 
                  className="btn-primary mr-4" 
                  onClick={() => navigate('/reservation')}
                  size="lg"
                >
                  Réserver une table
                </Button>
                <Button 
                  variant="outline" 
                  className="btn-secondary"
                  onClick={() => navigate('/menu')}
                  size="lg"
                >
                  Découvrir notre carte
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const MenuPage = () => {
  const { language, t } = React.useContext(LanguageContext);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if menu is cached
    const cachedMenu = localStorage.getItem('menu_cache');
    const cacheTimestamp = localStorage.getItem('menu_cache_timestamp');
    const cacheVersion = localStorage.getItem('menu_cache_version');
    const currentVersion = '1.0'; // Incrémenter pour forcer un rafraîchissement
    
    const isCacheValid = cachedMenu && cacheTimestamp && cacheVersion === currentVersion &&
                        (Date.now() - parseInt(cacheTimestamp)) < 24 * 60 * 60 * 1000; // 24h

    if (isCacheValid) {
      const cached = JSON.parse(cachedMenu);
      setCategories(cached.categories);
      setItems(cached.items);
      setLoading(false);
    } else {
      // Load from API and cache
      Promise.all([
        axios.get(`${API}/menu-categories`),
        axios.get(`${API}/menu-items`)
      ])
        .then(([catRes, itemRes]) => {
          setCategories(catRes.data);
          setItems(itemRes.data);
          
          // Cache the menu
          localStorage.setItem('menu_cache', JSON.stringify({
            categories: catRes.data,
            items: itemRes.data
          }));
          localStorage.setItem('menu_cache_timestamp', Date.now().toString());
          localStorage.setItem('menu_cache_version', currentVersion);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-burgundy text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl md:text-6xl font-display font-bold text-burgundy text-center mb-12" data-testid="menu-title">
          {t.ourMenu}
        </h1>

        <div className="space-y-16">
          {categories.map((category) => {
            const categoryItems = items.filter(item => item.category_id === category.id && item.available);
            if (categoryItems.length === 0) return null;

            return (
              <div key={category.id} className="menu-category" data-testid={`category-${category.id}`}>
                <h2 className="text-3xl md:text-4xl font-display font-bold text-burgundy mb-8 border-b-2 border-gold pb-3">
                  {category.name[language] || category.name.fr}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {categoryItems.map((item) => (
                    <Card key={item.id} className="menu-item-card" data-testid={`menu-item-${item.id}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-xl font-semibold text-burgundy">
                            {item.name[language] || item.name.fr}
                          </CardTitle>
                          <span className="text-gold font-bold text-lg">{item.price}€</span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{item.description[language] || item.description.fr}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {categories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">Le menu sera bientôt disponible.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfirmationPage = () => {
  const { t } = React.useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const reservationData = location.state?.reservationData;

  // Si pas de données de réservation, rediriger vers l'accueil
  useEffect(() => {
    if (!reservationData) {
      navigate('/');
    }
  }, [reservationData, navigate]);

  if (!reservationData) return null;

  // Formater la date en français
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Icône de confirmation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-burgundy mb-4">
            Réservation Confirmée !
          </h1>
          <p className="text-xl text-gray-700">
            Merci {reservationData.name}, votre réservation a été enregistrée avec succès.
          </p>
        </div>

        {/* Détails de la réservation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-burgundy">Détails de votre réservation</CardTitle>
            <CardDescription>Un email de confirmation vous a été envoyé à {reservationData.email}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Calendar className="text-burgundy mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">Date</p>
                  <p className="text-gray-600">{formatDate(reservationData.date)}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Clock className="text-burgundy mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">Heure</p>
                  <p className="text-gray-600">{reservationData.time}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Users className="text-burgundy mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">Nombre de personnes</p>
                  <p className="text-gray-600">{reservationData.guests} {reservationData.guests > 1 ? 'personnes' : 'personne'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="text-burgundy mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">Téléphone</p>
                  <p className="text-gray-600">{reservationData.phone}</p>
                </div>
              </div>
            </div>

            {reservationData.message && (
              <div className="pt-4 border-t">
                <p className="font-semibold text-gray-900 mb-2">Message</p>
                <p className="text-gray-600 italic">"{reservationData.message}"</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Informations pratiques */}
        <Card className="mb-8 bg-burgundy/5 border-burgundy/20">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-burgundy mb-3">Informations pratiques</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-burgundy">•</span>
                <span>Merci d'arriver à l'heure pour profiter pleinement de votre expérience</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-burgundy">•</span>
                <span>En cas d'empêchement, merci de nous prévenir au plus tôt</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-burgundy">•</span>
                <span>Nous vous attendons avec impatience au Parvis Pompidou !</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Boutons d'action */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            className="btn-primary"
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="btn-secondary"
            onClick={() => navigate('/menu')}
          >
            Découvrir le menu
          </Button>
        </div>
      </div>
    </div>
  );
};

const ReservationPage = () => {
  const { t } = React.useContext(LanguageContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: 2,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showGroupBookingMessage, setShowGroupBookingMessage] = useState(false);

  // Générer les créneaux horaires de 10h à 00h
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 10; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    slots.push('00:00'); // Minuit
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleGuestsChange = (value) => {
    const guests = parseInt(value);
    if (guests > 12) {
      setShowGroupBookingMessage(true);
      setFormData({ ...formData, guests: 12 });
    } else {
      setShowGroupBookingMessage(false);
      setFormData({ ...formData, guests });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.guests > 12) {
      toast.error('Pour les réservations de plus de 12 personnes, veuillez nous contacter directement.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, formData);
      // Rediriger vers la page de confirmation avec les données de réservation
      navigate('/confirmation', { state: { reservationData: formData } });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la réservation');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-5xl md:text-6xl font-display font-bold text-burgundy text-center mb-12" data-testid="reservation-title">
          {t.reserveTable}
        </h1>

        <Card className="reservation-card">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">{t.name}</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="reservation-name"
                />
              </div>

              <div>
                <Label htmlFor="email">{t.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="reservation-email"
                />
              </div>

              <div>
                <Label htmlFor="phone">{t.phone}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="reservation-phone"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">{t.date}</Label>
                  <Input
                    id="date"
                    type="date"
                    min={today}
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="reservation-date"
                  />
                </div>
                <div>
                  <Label htmlFor="time">{t.time}</Label>
                  <select
                    id="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-burgundy"
                    data-testid="reservation-time"
                  >
                    <option value="">Sélectionnez une heure</option>
                    {timeSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Horaires : 10h00 - 00h00</p>
                </div>
              </div>

              <div>
                <Label htmlFor="guests">{t.guests}</Label>
                <Input
                  id="guests"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.guests}
                  onChange={(e) => handleGuestsChange(e.target.value)}
                  required
                  data-testid="reservation-guests"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum 12 personnes, pour les réservations de groupe ou privatisation contactez-nous par téléphone ou par mail.</p>
                
                {showGroupBookingMessage && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800 font-semibold">Réservation de groupe</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Pour les groupes de plus de 12 personnes, veuillez nous contacter directement au{' '}
                      <a href="tel:0146069515" className="underline font-semibold">01 46 06 95 15</a>
                      {' '}ou par email à{' '}
                      <a href="mailto:leparvisbeaubourg@gmail.com" className="underline font-semibold">
                        leparvisbeaubourg@gmail.com
                      </a>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="message">{t.message}</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Allergies, préférences de table, occasion spéciale..."
                  data-testid="reservation-message"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>📋 Important :</strong>
                </p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4 list-disc">
                  <li>Les réservations sont confirmées sous 24h</li>
                  <li>En cas d'empêchement, merci de nous prévenir</li>
                  <li>Tables disponibles de 10h00 à 00h00</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={submitting}
                data-testid="reservation-submit"
              >
                {submitting ? 'Envoi...' : t.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ReviewsPage = () => {
  const { t } = React.useContext(LanguageContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(res => setReviews(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-burgundy text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl md:text-6xl font-display font-bold text-burgundy text-center mb-8" data-testid="reviews-title">
          {t.customerReviews}
        </h1>

        {/* Badge Google */}
        <div className="flex justify-center mb-12">
          <a
            href="https://www.google.com/maps/place/Le+C%C3%A9page+Montmartrois/@48.8898889,2.3338333,17z"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 bg-white px-8 py-5 rounded-lg shadow-lg hover:shadow-xl transition border border-gray-200"
          >
            <img 
              src="https://www.gstatic.com/images/branding/googlelogo/svg/googlelogo_clr_74x24px.svg" 
              alt="Google"
              className="h-10"
            />
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className={i < 4 ? 'fill-yellow-500 text-yellow-500' : 'text-yellow-500'} />
                ))}
              </div>
              <p className="text-lg font-semibold text-gray-800">2,100+ avis - 4.0/5</p>
            </div>
          </a>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {reviews.map((review) => (
            <Card key={review.id} className="review-card" data-testid={`review-card-${review.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg">{review.author_name}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{review.source}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className={i < review.rating ? 'fill-gold text-gold' : 'text-gray-300'} />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({review.rating}/5)</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 italic mb-3">"{review.text}"</p>
                <p className="text-sm text-gray-400">{review.date}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {reviews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">Aucun avis pour le moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ContactPage = () => {
  const { language, t } = React.useContext(LanguageContext);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(console.error);
  }, []);

  if (!settings) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl md:text-6xl font-display font-bold text-burgundy text-center mb-12" data-testid="contact-title">
          {t.contact}
        </h1>

        <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-burgundy flex items-center gap-2">
                <MapPin size={24} /> {t.address}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{settings.address}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-burgundy flex items-center gap-2">
                <Clock size={24} /> {t.openingHours}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{settings.opening_hours[language] || settings.opening_hours.fr}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-burgundy flex items-center gap-2">
                <Phone size={24} /> {t.phone}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <a href={`tel:${settings.phone}`} className="text-gray-700 hover:text-burgundy transition">
                {settings.phone}
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-burgundy flex items-center gap-2">
                <Mail size={24} /> Email
              </CardTitle>
            </CardHeader>
            <CardContent>
              <a href={`mailto:${settings.email}`} className="text-gray-700 hover:text-burgundy transition">
                {settings.email}
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const { t } = React.useContext(LanguageContext);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      localStorage.setItem('admin_token', response.data.access_token);
      onLogin(response.data.access_token);
      toast.success('Connexion réussie!');
    } catch (error) {
      toast.error('Identifiants incorrects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-burgundy text-center" data-testid="admin-login-title">{t.loginAdmin}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">{t.username}</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                data-testid="admin-password"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="admin-login-submit">
              {loading ? 'Connexion...' : t.login}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('reservations');
  const [reservations, setReservations] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedCategoriesToReplace, setSelectedCategoriesToReplace] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [fetchingInstagram, setFetchingInstagram] = useState(false);
  const [igUploading, setIgUploading] = useState(false);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const reservationsPerPage = 20;

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [resRes, catRes, itemRes, revRes, igRes, clientsRes] = await Promise.all([
        axios.get(`${API}/reservations`, config),
        axios.get(`${API}/menu-categories`),
        axios.get(`${API}/menu-items`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/instagram/posts`),
        axios.get(`${API}/clients`, config)
      ]);
      setReservations(resRes.data);
      setCategories(catRes.data);
      setMenuItems(itemRes.data);
      setReviews(revRes.data);
      setInstagramPosts(igRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        onLogout();
      }
    }
  };

  const updateReservationStatus = async (id, status) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.patch(`${API}/reservations/${id}`, { status }, config);
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteReservation = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/reservations/${id}`, config);
      toast.success('Réservation supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    setUploadProgress(2);
    setUploadStage('Démarrage de l\'analyse...');
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('categories_to_replace', JSON.stringify(selectedCategoriesToReplace));

    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const startResp = await axios.post(`${API}/menu/upload-ocr`, formData, {
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      const jobId = startResp.data.job_id;
      if (!jobId) throw new Error('Impossible de démarrer l\'analyse');

      // Poll for progress
      await new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const { data } = await axios.get(`${API}/menu/upload-ocr/status/${jobId}`, config);
            setUploadProgress(data.progress || 0);
            setUploadStage(data.message || '');

            if (data.status === 'completed') {
              clearInterval(interval);
              toast.success(data.message);
              setUploadFile(null);
              setSelectedCategoriesToReplace([]);
              const fileInput = document.getElementById('file');
              if (fileInput) fileInput.value = '';
              // Clear menu cache so visitors see the new menu
              localStorage.removeItem('menu_cache');
              localStorage.removeItem('menu_cache_timestamp');
              localStorage.removeItem('menu_cache_version');
              loadData();
              resolve();
            } else if (data.status === 'error') {
              clearInterval(interval);
              toast.error('Erreur: ' + (data.error || 'Analyse échouée'));
              reject(new Error(data.error));
            }
          } catch (pollErr) {
            clearInterval(interval);
            reject(pollErr);
          }
        }, 1500);
      });
    } catch (error) {
      toast.error('Erreur lors de l\'upload: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const toggleCategorySelection = (catId) => {
    setSelectedCategoriesToReplace(prev => 
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const deleteMenuItem = async (itemId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet item ?')) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/menu-items/${itemId}`, config);
      toast.success('Item supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteCategory = async (catId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette catégorie et tous ses items ?')) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/menu-categories/${catId}`, config);
      toast.success('Catégorie supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const startEditItem = (item) => {
    setEditingItem({...item});
    setShowEditDialog(true);
  };

  const saveEditItem = async () => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.patch(`${API}/menu-items/${editingItem.id}`, {
        category_id: editingItem.category_id,
        name: editingItem.name,
        description: editingItem.description,
        price: parseFloat(editingItem.price),
        available: editingItem.available,
        order: editingItem.order
      }, config);
      toast.success('Item mis à jour');
      setShowEditDialog(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const moveCategoryUp = async (category) => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (currentIndex <= 0) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const prevCategory = categories[currentIndex - 1];
      
      // Swap orders
      await axios.patch(`${API}/menu-categories/${category.id}`, 
        { name: category.name, order: prevCategory.order }, config);
      await axios.patch(`${API}/menu-categories/${prevCategory.id}`, 
        { name: prevCategory.name, order: category.order }, config);
      
      toast.success('Ordre modifié');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const moveCategoryDown = async (category) => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (currentIndex >= categories.length - 1) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const nextCategory = categories[currentIndex + 1];
      
      // Swap orders
      await axios.patch(`${API}/menu-categories/${category.id}`, 
        { name: category.name, order: nextCategory.order }, config);
      await axios.patch(`${API}/menu-categories/${nextCategory.id}`, 
        { name: nextCategory.name, order: category.order }, config);
      
      toast.success('Ordre modifié');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const fetchInstagramPosts = async () => {
    setFetchingInstagram(true);
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await axios.post(`${API}/instagram/fetch`, {}, config);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la récupération des posts Instagram');
      console.error(error);
    } finally {
      setFetchingInstagram(false);
    }
  };

  const deleteInstagramPost = async (postId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce post ?')) return;
    
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.delete(`${API}/instagram/posts/${postId}`, config);
      toast.success('Post supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleInstagramUpload = async (file) => {
    if (!file) return;
    setIgUploading(true);
    const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } };
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/instagram/upload`, fd, config);
      toast.success('Photo ajoutée à la galerie !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la photo');
      console.error(error);
    } finally {
      setIgUploading(false);
    }
  };

  const addInstagramPost = async (imageUrl, postUrl, caption) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      await axios.post(`${API}/instagram/posts`, { 
        image_url: imageUrl,
        post_url: postUrl || 'https://www.instagram.com/leparvis.beaubourg/',
        caption: caption || ''
      }, config);
      toast.success('Image ajoutée à la galerie !');
      loadData();
      return true;
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'image');
      console.error(error);
      return false;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-cream">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-display font-bold text-burgundy" data-testid="admin-dashboard-title">Tableau de bord Admin</h1>
          <Button onClick={onLogout} variant="outline" data-testid="admin-logout">Déconnexion</Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Onglets avec scroll horizontal sur mobile */}
          <div className="w-full overflow-x-auto mb-6">
            <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-7 gap-1 p-1">
              <TabsTrigger value="reservations" data-testid="tab-reservations" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Réservations
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Calendrier
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Clients
              </TabsTrigger>
              <TabsTrigger value="menu" data-testid="tab-menu" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Menu
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Avis
              </TabsTrigger>
              <TabsTrigger value="instagram" data-testid="tab-instagram" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Instagram
              </TabsTrigger>
              <TabsTrigger value="upload" data-testid="tab-upload" className="whitespace-nowrap px-3 py-2 text-xs md:text-sm">
                Upload OCR
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="reservations" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-burgundy">Réservations - {reservations.length} total</h2>
            </div>
            
            {(() => {
              const indexOfLastReservation = currentPage * reservationsPerPage;
              const indexOfFirstReservation = indexOfLastReservation - reservationsPerPage;
              const currentReservations = reservations.slice(indexOfFirstReservation, indexOfLastReservation);
              const totalPages = Math.ceil(reservations.length / reservationsPerPage);
              
              return (
                <>
                  {currentReservations.map((res) => (
                    <Card key={res.id} data-testid={`reservation-card-${res.id}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle>{res.name}</CardTitle>
                            <CardDescription>{res.email} • {res.phone}</CardDescription>
                          </div>
                          <Badge
                            variant={res.status === 'confirmed' ? 'default' : res.status === 'cancelled' ? 'destructive' : 'secondary'}
                          >
                            {res.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                          <p><strong>Date:</strong> {res.date}</p>
                          <p><strong>Heure:</strong> {res.time}</p>
                          <p><strong>Personnes:</strong> {res.guests}</p>
                          <p><strong>Créée le:</strong> {new Date(res.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        {res.message && <p className="text-sm text-gray-600 mb-4"><strong>Message:</strong> {res.message}</p>}
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => updateReservationStatus(res.id, 'confirmed')} data-testid={`confirm-${res.id}`}>
                            Confirmer
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateReservationStatus(res.id, 'cancelled')} data-testid={`cancel-${res.id}`}>
                            Annuler
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteReservation(res.id)} data-testid={`delete-${res.id}`}>
                            Supprimer
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Précédent
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                      </Button>
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="calendar" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-burgundy mb-4">Calendrier des réservations</h2>
              <p className="text-gray-600 mb-4">Vue mensuelle de toutes les réservations</p>
            </div>
            
            {(() => {
              // Grouper les réservations par date
              const reservationsByDate = reservations.reduce((acc, res) => {
                const date = res.date;
                if (!acc[date]) acc[date] = [];
                acc[date].push(res);
                return acc;
              }, {});
              
              // Trier les dates
              const sortedDates = Object.keys(reservationsByDate).sort((a, b) => new Date(b) - new Date(a));
              
              return (
                <div className="space-y-4">
                  {sortedDates.map(date => (
                    <Card key={date}>
                      <CardHeader className="bg-burgundy/10">
                        <CardTitle className="text-burgundy">
                          {new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                          <Badge variant="secondary" className="ml-3">{reservationsByDate[date].length} réservation(s)</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-2">
                          {reservationsByDate[date]
                            .sort((a, b) => a.time.localeCompare(b.time))
                            .map(res => (
                              <div key={res.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                <div className="flex items-center gap-4">
                                  <span className="font-bold text-burgundy">{res.time}</span>
                                  <span className="font-semibold">{res.name}</span>
                                  <span className="text-gray-600">{res.guests} pers.</span>
                                  <Badge variant={res.status === 'confirmed' ? 'default' : 'destructive'}>
                                    {res.status}
                                  </Badge>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {res.email}
                                </div>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {sortedDates.length === 0 && (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <p className="text-gray-500">Aucune réservation</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="clients" className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-burgundy mb-2">Fichier Clients - {clients.length} clients</h2>
              <p className="text-gray-600">Historique automatique de tous vos clients</p>
            </div>
            
            <div className="grid gap-4">
              {clients.map((client) => (
                <Card key={client.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{client.name}</CardTitle>
                        <CardDescription>{client.email} • {client.phone}</CardDescription>
                      </div>
                      <Badge variant="secondary">
                        {client.total_reservations} visite(s)
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Première visite</p>
                        <p className="font-semibold">{client.first_visit ? new Date(client.first_visit + 'T00:00:00').toLocaleDateString('fr-FR') : '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Dernière visite</p>
                        <p className="font-semibold">{client.last_visit ? new Date(client.last_visit + 'T00:00:00').toLocaleDateString('fr-FR') : '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {clients.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-500">Aucun client enregistré</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="menu" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-burgundy">Gestion du menu - {menuItems.length} items</h2>
            </div>

            {categories.map((category, index) => {
              const categoryItems = menuItems.filter(item => item.category_id === category.id);
              return (
                <Card key={category.id} className="overflow-hidden">
                  <CardHeader className="bg-burgundy text-cream">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveCategoryUp(category)}
                            disabled={index === 0}
                            className="h-6 w-8 p-0 text-cream hover:text-gold hover:bg-burgundy/80"
                            title="Monter"
                          >
                            ▲
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveCategoryDown(category)}
                            disabled={index === categories.length - 1}
                            className="h-6 w-8 p-0 text-cream hover:text-gold hover:bg-burgundy/80"
                            title="Descendre"
                          >
                            ▼
                          </Button>
                        </div>
                        <CardTitle className="text-xl">
                          {category.name.fr} ({categoryItems.length} items)
                        </CardTitle>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteCategory(category.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Supprimer catégorie
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {categoryItems.length === 0 ? (
                      <div className="p-4 text-gray-500 text-center">
                        Aucun item dans cette catégorie
                      </div>
                    ) : (
                      <div className="divide-y">
                        {categoryItems.map((item) => (
                          <div key={item.id} className="p-4 hover:bg-gray-50">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold text-lg">{item.name.fr}</h4>
                                  <span className="text-gold font-bold">{item.price}€</span>
                                  <Badge variant={item.available ? "default" : "secondary"}>
                                    {item.available ? "Disponible" : "Indisponible"}
                                  </Badge>
                                </div>
                                <p className="text-gray-600 text-sm mb-2">{item.description.fr}</p>
                                <div className="text-xs text-gray-400">
                                  Ordre: {item.order} | ID: {item.id}
                                </div>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditItem(item)}
                                >
                                  Modifier
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deleteMenuItem(item.id)}
                                >
                                  Supprimer
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {categories.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-gray-500">Aucune catégorie de menu trouvée.</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Utilisez l'onglet "Upload OCR" pour créer votre menu.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Modifier l'item de menu</DialogTitle>
                  <DialogDescription>
                    Modifiez les informations de l'item de menu
                  </DialogDescription>
                </DialogHeader>
                {editingItem && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-category">Catégorie</Label>
                      <select
                        id="edit-category"
                        value={editingItem.category_id}
                        onChange={(e) => setEditingItem({...editingItem, category_id: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-md"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name.fr}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-name">Nom (FR)</Label>
                      <Input
                        id="edit-name"
                        value={editingItem.name.fr}
                        onChange={(e) => setEditingItem({
                          ...editingItem, 
                          name: {...editingItem.name, fr: e.target.value}
                        })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-description">Description (FR)</Label>
                      <Textarea
                        id="edit-description"
                        value={editingItem.description.fr}
                        onChange={(e) => setEditingItem({
                          ...editingItem, 
                          description: {...editingItem.description, fr: e.target.value}
                        })}
                        rows={3}
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-price">Prix (€)</Label>
                        <Input
                          id="edit-price"
                          type="number"
                          step="0.01"
                          value={editingItem.price}
                          onChange={(e) => setEditingItem({...editingItem, price: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-order">Ordre</Label>
                        <Input
                          id="edit-order"
                          type="number"
                          value={editingItem.order}
                          onChange={(e) => setEditingItem({...editingItem, order: parseInt(e.target.value)})}
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2 pt-6">
                        <input
                          type="checkbox"
                          id="edit-available"
                          checked={editingItem.available}
                          onChange={(e) => setEditingItem({...editingItem, available: e.target.checked})}
                        />
                        <Label htmlFor="edit-available">Disponible</Label>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                        Annuler
                      </Button>
                      <Button onClick={saveEditItem}>
                        Sauvegarder
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="reviews">
            <p className="text-gray-600">Gestion des avis - {reviews.length} avis</p>
          </TabsContent>

          <TabsContent value="instagram" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-burgundy">Galerie Instagram - {instagramPosts.length} photos</h2>
            </div>

            {/* Upload direct depuis l'appareil (recommandé) */}
            <Card className="mb-6 border-burgundy/30">
              <CardHeader>
                <CardTitle>Ajouter une photo (recommandé)</CardTitle>
                <CardDescription>Choisissez une photo depuis votre téléphone ou ordinateur. Elle est hébergée sur notre serveur et s'affiche de façon fiable et permanente.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Label
                  htmlFor="ig-upload-input"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-burgundy/40 rounded-lg p-8 cursor-pointer hover:bg-cream/50 transition"
                  data-testid="instagram-upload-dropzone"
                >
                  <Instagram size={28} className="text-burgundy" />
                  <span className="text-sm text-gray-700 font-medium">
                    {igUploading ? 'Envoi en cours...' : 'Cliquez pour choisir une photo'}
                  </span>
                  <span className="text-xs text-gray-400">JPG, PNG — 6 photos recommandées</span>
                </Label>
                <Input
                  id="ig-upload-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={igUploading}
                  onChange={(e) => { handleInstagramUpload(e.target.files[0]); e.target.value = ''; }}
                  data-testid="instagram-upload-input"
                />
              </CardContent>
            </Card>

            <div className="text-center text-sm text-gray-400 my-2">— ou ajouter via une URL d'image (déconseillé pour Instagram) —</div>


            {/* Formulaire d'ajout manuel */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Ajouter une image à la galerie</CardTitle>
                <CardDescription>Collez l'URL publique d'une image (ex. depuis votre site ou une banque d'images). ⚠️ Les liens d'images directement copiés depuis Instagram ne fonctionnent pas (ils expirent) — utilisez plutôt l'upload ci-dessus.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const imageUrl = formData.get('image_url');
                  const postUrl = formData.get('post_url');
                  const caption = formData.get('caption');
                  
                  if (await addInstagramPost(imageUrl, postUrl, caption)) {
                    e.target.reset();
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="image_url">URL de l'image *</Label>
                    <Input
                      id="image_url"
                      name="image_url"
                      type="url"
                      placeholder="https://instagram.com/.../image.jpg"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Clic droit sur l'image Instagram → "Copier l'adresse de l'image"
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="post_url">URL du post Instagram (optionnel)</Label>
                    <Input
                      id="post_url"
                      name="post_url"
                      type="url"
                      placeholder="https://www.instagram.com/p/..."
                      defaultValue="https://www.instagram.com/leparvis.beaubourg/"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="caption">Légende (optionnel)</Label>
                    <Textarea
                      id="caption"
                      name="caption"
                      placeholder="Courte description de l'image"
                      rows={2}
                    />
                  </div>
                  
                  <Button type="submit" className="btn-primary">
                    Ajouter à la galerie
                  </Button>
                </form>
              </CardContent>
            </Card>

            {instagramPosts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-gray-500 mb-4">Aucune photo dans la galerie.</p>
                  <p className="text-sm text-gray-400">Ajoutez des images avec le formulaire ci-dessus</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {instagramPosts.map((post) => (
                  <Card key={post.id} className="overflow-hidden">
                    <img 
                      src={post.image_url?.startsWith('/') ? `${BACKEND_URL}${post.image_url}` : post.image_url} 
                      alt={post.caption || 'Instagram post'} 
                      className="w-full h-[250px] object-cover"
                    />
                    <CardContent className="p-4">
                      {post.caption && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.caption}</p>
                      )}
                      <div className="flex justify-between items-center text-xs text-gray-400">
                        <span>{post.date}</span>
                        {post.likes && <span>❤️ {post.likes}</span>}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(post.post_url, '_blank')}
                          className="flex-1"
                        >
                          Voir sur Instagram
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteInstagramPost(post.id)}
                        >
                          Supprimer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6">
              <p className="text-sm text-blue-800 mb-2">
                <strong>ℹ️ Comment obtenir les URLs des images Instagram :</strong>
              </p>
              <ol className="text-sm text-blue-700 space-y-1 ml-4 list-decimal">
                <li>Allez sur <a href="https://www.instagram.com/leparvis.beaubourg/" target="_blank" rel="noopener noreferrer" className="underline">votre profil Instagram</a></li>
                <li>Cliquez sur une photo pour l'ouvrir</li>
                <li>Clic droit sur l'image → "Copier l'adresse de l'image"</li>
                <li>Collez l'URL dans le formulaire ci-dessus</li>
              </ol>
              <p className="text-xs text-blue-600 mt-2">
                💡 Les 6 dernières images ajoutées seront affichées dans la galerie de la page d'accueil
              </p>
            </div>
          </TabsContent>

          <TabsContent value="upload">
            <Card>
              <CardHeader>
                <CardTitle>Upload Menu avec OCR</CardTitle>
                <CardDescription>Téléchargez une photo du menu - Choisissez les catégories à remplacer</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Bouton pour vider le cache */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-blue-800">Cache du menu</p>
                      <p className="text-sm text-blue-600">Le menu est mis en cache pour améliorer les performances. Videz le cache après chaque mise à jour.</p>
                    </div>
                    <Button 
                      onClick={() => {
                        localStorage.removeItem('menu_cache');
                        localStorage.removeItem('menu_cache_timestamp');
                        localStorage.removeItem('menu_cache_version');
                        toast.success('Cache vidé! Les visiteurs verront le nouveau menu.');
                      }}
                      variant="outline"
                      className="ml-4"
                    >
                      Vider le cache
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleFileUpload} className="space-y-6">
                  <div>
                    <Label htmlFor="file">Photo du menu</Label>
                    <Input
                      id="file"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      required
                      data-testid="upload-file-input"
                    />
                    {uploadFile && (
                      <p className="text-sm text-gray-600 mt-2">
                        Fichier sélectionné: {uploadFile.name}
                      </p>
                    )}
                  </div>

                  {categories.length > 0 && (
                    <div>
                      <Label className="text-base font-semibold">Catégories à remplacer (optionnel)</Label>
                      <p className="text-sm text-gray-600 mb-3">
                        Sélectionnez les catégories existantes que vous voulez remplacer. 
                        Si aucune n'est sélectionnée, de nouvelles catégories seront créées.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {categories.map((category) => (
                          <div key={category.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`cat-${category.id}`}
                              checked={selectedCategoriesToReplace.includes(category.id)}
                              onChange={() => toggleCategorySelection(category.id)}
                              className="rounded border-gray-300"
                            />
                            <Label 
                              htmlFor={`cat-${category.id}`} 
                              className="text-sm cursor-pointer"
                            >
                              {category.name.fr}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {selectedCategoriesToReplace.length > 0 && (
                        <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                          <p className="text-sm text-orange-800">
                            <strong>⚠️ Attention:</strong> Les catégories sélectionnées et tous leurs items seront supprimés et remplacés.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-sm text-blue-800 font-semibold">ℹ️ Mode de fonctionnement</p>
                    <div className="text-sm text-blue-700 mt-2 space-y-1">
                      {selectedCategoriesToReplace.length > 0 ? (
                        <>
                          <p>• Les catégories sélectionnées seront supprimées</p>
                          <p>• Le nouveau contenu détecté les remplacera</p>
                          <p>• Les autres catégories existantes seront conservées</p>
                        </>
                      ) : (
                        <>
                          <p>• Le contenu détecté sera ajouté comme nouvelles catégories</p>
                          <p>• Toutes les catégories existantes seront conservées</p>
                          <p>• Aucune suppression ne sera effectuée</p>
                        </>
                      )}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary" 
                    disabled={uploading}
                    data-testid="upload-submit-button"
                  >
                    {uploading ? 'Analyse et traitement en cours...' : 
                     selectedCategoriesToReplace.length > 0 ? 
                     `Remplacer ${selectedCategoriesToReplace.length} catégorie(s)` : 
                     'Ajouter nouveau contenu'}
                  </Button>

                  {uploading && (
                    <div className="space-y-2 pt-2" data-testid="ocr-progress-container">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700" data-testid="ocr-progress-stage">{uploadStage}</span>
                        <span className="font-semibold text-gray-900" data-testid="ocr-progress-percent">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-3" data-testid="ocr-progress-bar" />
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600">
                    <p><strong>Le système OCR va automatiquement:</strong></p>
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li>Détecter les catégories et items dans l'image</li>
                      <li>Extraire les noms, descriptions et prix</li>
                      <li>Traduire automatiquement en 11 langues (FR, EN, ES, IT, DE, PT, RU, ZH, JA, TH, AR)</li>
                      <li>Intégrer le contenu selon votre sélection</li>
                    </ul>
                    <p className="mt-3 text-xs text-gray-500">
                      💡 Conseil: Prenez une photo claire avec un bon éclairage pour de meilleurs résultats
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  return token ? (
    <AdminDashboard token={token} onLogout={handleLogout} />
  ) : (
    <AdminLogin onLogin={setToken} />
  );
};

function App() {
  const [language, setLanguage] = useState('fr');
  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <div className="App">
        <BrowserRouter>
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/reservation" element={<ReservationPage />} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </LanguageContext.Provider>
  );
}

export default App;